<script setup lang="ts">
import { fixNumber } from "@/components/Mission/MissionStat";
import { useNotification } from "@/stores/notification";
import { Game } from "phaser";
import { storeToRefs } from "pinia";
import { showConfirmDialog, showLoadingToast } from "vant";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { HouseScene } from "../../scenes/HouseScene";
import { useHouseScene } from "../../store/houseScene";
import { HouseSessionTab, useHouseSession } from "../../store/houseSession";
import ChatPanel from "../ChatPanel.vue";
import EventPanel from "./event/EventPanel.vue";
import InteractionPanel from "./InteractionPanel.vue";
import MemberPanel from "./MemberPanel.vue";
import { useRouter } from "vue-router";

defineProps<{
  loading: boolean;
  scene?: HouseScene;
  game?: Phaser.Game;
}>();

const emit = defineEmits<{
  (e: "sprite-click", spriteId: number): void;
}>();

const router = useRouter();
const houseStore = useHouseSession();
const houseSceneStore = useHouseScene();
const { t } = useI18n();
// const interactStore = useInteractive();
const { isThoughtActivated } = storeToRefs(houseStore);
const notification = useNotification();

const subjectLength = computed(() => {
  return houseStore.houseSession?.subject?.length || 0;
});
const extraPower = computed(() => {
  if (!houseStore.houseSession?.subject) return 0;
  return fixNumber(Math.ceil((subjectLength.value - 100) / 100) * 0.1);
});
const showHouseSetting = ref(false);

// Lifecycle

// methods
const onToggleAllActiveThoughtClick = async (toggle?: boolean) => {
  const isEnable = toggle ?? !isThoughtActivated.value;

  if (isEnable) {
    const pass = await houseSceneStore.checkPowerAmountForThought();
    if (!pass) {
      return;
    }
  }
  try {
    await showConfirmDialog({
      title: t("house.thoughtToggle.title", {
        toggle: isEnable
          ? t("house.thoughtToggle.active")
          : t("house.thoughtToggle.deactive"),
      }),
      message: isEnable
        ? t("house.thoughtToggle.activeTip")
        : t("house.thoughtToggle.deactiveTip"),
    });
    const loading = showLoadingToast(t("Message.saving"));
    try {
      await houseStore.toggleAllActiveThought(isEnable);
    } finally {
      loading.close();
    }
  } catch {
    // cancel ignore
    return;
  }
};
// const onToggleInteraction = async () => {
//   const isEnable = !interactStore.isPoolEnabled;
//   try {
//     await showConfirmDialog({
//       title: t("house.interactToggle.title", {
//         toggle: isEnable
//           ? t("house.interactToggle.active")
//           : t("house.interactToggle.deactive"),
//       }),
//       message: isEnable
//         ? t("house.interactToggle.activeTip")
//         : t("house.interactToggle.deactiveTip"),
//     });
//     await interactStore.toggleUserInteractionPoolStatus();
//   } catch {
//     // cancel ignore
//     return;
//   }
// };
const onTabChange = (tab: HouseSessionTab) => {
  router.replace({ query: { tab } });
};
</script>
<template>
  <div class="bg-base-300 grid grid-rows-[auto_1fr] h-full w-full">
    <div class="flex justify-between items-center bg-base-100">
      <van-tabs
        v-model:active="houseStore.activeTab"
        shrink
        line-width="20px"
        @change="onTabChange"
      >
        <van-tab :title="$t('house.member')" name="members" />
        <!-- <van-tab :title="$t('house.subject')" name="subject" /> -->
        <van-tab :title="$t('house.thoughts')" name="thoughts" />
        <van-tab
          :title="$t('house.events')"
          :dot="!!houseStore.generatedButNotPlayedEvent"
          name="events"
        />
        <van-tab
          :title="$t('house.interaction')"
          :badge="notification.displayInteractionPoolSummary || undefined"
          name="interaction"
        />
      </van-tabs>
      <div class="join mr-2"></div>
    </div>
    <MemberPanel
      v-if="houseStore.activeTab === 'members'"
      :loading="loading"
      :extra-power="extraPower"
      @sprite-click="emit('sprite-click', $event)"
      @show-house-setting="showHouseSetting = true"
      @toggle-all-acitve-thought="onToggleAllActiveThoughtClick()"
    />
    <ChatPanel
      v-if="houseStore.activeTab === 'thoughts' && !loading"
      @enable-all-active-thought="onToggleAllActiveThoughtClick(true)"
    />
    <EventPanel
      v-if="houseStore.activeTab === 'events'"
      :game="game! as Game"
    />

    <InteractionPanel v-if="houseStore.activeTab === 'interaction'" />
  </div>
</template>
<style scoped>
.action-btn {
  @apply btn text-xs rounded flex flex-col gap-2 items-center;
}
</style>
